import { dump, load, YAMLException } from 'js-yaml';

/**
 * Pure helpers for the YAML formatter / converter tool.
 *
 * Wraps `js-yaml`:
 *   - parse / format / minify with consistent { ok, ... } discriminated
 *     unions and 1-based (line, column) on errors so callers can drop
 *     the result straight into Monaco's setModelMarkers.
 *   - yaml ↔ json round-trip with optional indent.
 *
 * R3 reminder: schema is fixed at js-yaml's `DEFAULT_SCHEMA`. Strict
 * (`CORE_SCHEMA` / `FAILSAFE_SCHEMA`) is a P1 add — see
 * docs/web整站功能/devtools-categories/01-format/yaml.md §12.
 */

export interface FormatOk {
  ok: true;
  output: string;
}

export interface FormatErr {
  ok: false;
  error: string;
  /** 1-based line number from js-yaml's mark, when available. */
  line?: number;
  /** 1-based column number. */
  column?: number;
}

export type YamlResult = FormatOk | FormatErr;

export interface YamlFormatOptions {
  /** Indent width for block style (2..10, default 2). */
  indent?: number;
  /**
   * Force flow style at depth >= flowLevel. -1 (default) = always
   * block. 0 = always flow (= "minify"). Used internally by
   * `minifyYaml`.
   */
  flowLevel?: number;
}

export interface YamlToJsonOptions {
  /** JSON indent (0 = compact, default 2). */
  indent?: number;
}

export interface JsonToYamlOptions {
  /** YAML indent width (default 2). */
  indent?: number;
}

const DEFAULT_INDENT = 2;

/**
 * Parse a YAML document and return the JS value. Returns the canonical
 * pretty-printed YAML on success.
 *
 * Multi-document streams (`---`-separated) collapse to the **first**
 * document — matches the design doc decision and the most common case
 * (k8s manifest, helm template). Multi-document support is parked for
 * P1.
 */
export function formatYaml(input: string, opts: YamlFormatOptions = {}): YamlResult {
  if (input.trim().length === 0) return { ok: true, output: '' };
  try {
    const value = load(input, { schema: undefined });
    if (value === undefined) return { ok: true, output: '' };
    const out = dump(value, {
      indent: clampIndent(opts.indent),
      flowLevel: opts.flowLevel ?? -1,
      lineWidth: 120,
      noRefs: false,
      sortKeys: false,
      quotingType: '"',
      forceQuotes: false,
    });
    return { ok: true, output: out };
  } catch (e) {
    return toError(e);
  }
}

/**
 * Compress YAML to flow style (`{a: 1, b: [2, 3]}`). YAML doesn't have
 * a true "minify" — block style is already line-wrapped for clarity —
 * so we approximate by serialising with `flowLevel: 0`, which puts
 * every collection on a single line.
 */
export function minifyYaml(input: string): YamlResult {
  return formatYaml(input, { flowLevel: 0 });
}

/**
 * YAML → JSON. Empty input yields empty output (consistent with
 * formatYaml).
 */
export function yamlToJson(input: string, opts: YamlToJsonOptions = {}): YamlResult {
  if (input.trim().length === 0) return { ok: true, output: '' };
  try {
    const value = load(input);
    if (value === undefined) return { ok: true, output: '' };
    const indent = opts.indent ?? DEFAULT_INDENT;
    const out = JSON.stringify(value, null, indent);
    return { ok: true, output: out };
  } catch (e) {
    return toError(e);
  }
}

/**
 * JSON → YAML. Defers JSON syntax errors to the user the same way the
 * yaml path does: returns { ok: false, error, line, column? } when
 * possible, falling back to `error` only if the engine doesn't expose
 * a position.
 *
 * `indent` defaults to 2 to match formatYaml so a round-trip
 * `format → toJson → toYaml` stays visually identical.
 */
export function jsonToYaml(input: string, opts: JsonToYamlOptions = {}): YamlResult {
  if (input.trim().length === 0) return { ok: true, output: '' };
  let value: unknown;
  try {
    value = JSON.parse(input);
  } catch (e) {
    const err = e as Error;
    // V8 / JSC put a "at position N" hint in JSON.parse error messages.
    // We can recover (line, column) cheaply by counting newlines up to
    // that offset, which lets the marker land on the offending byte
    // instead of just line 1.
    const m = /at position (\d+)/.exec(err.message);
    if (m) {
      const offset = Number(m[1]);
      const slice = input.slice(0, offset);
      const line = slice.split('\n').length;
      const column = slice.length - slice.lastIndexOf('\n');
      return { ok: false, error: err.message, line, column };
    }
    return { ok: false, error: err.message };
  }
  try {
    const out = dump(value, {
      indent: clampIndent(opts.indent),
      lineWidth: 120,
      noRefs: false,
      sortKeys: false,
      quotingType: '"',
      forceQuotes: false,
    });
    return { ok: true, output: out };
  } catch (e) {
    return toError(e);
  }
}

/* ------------------------------------------------------------------ */

function toError(e: unknown): FormatErr {
  if (e instanceof YAMLException) {
    // js-yaml's `mark` is 0-based, our public API is 1-based.
    const line = e.mark?.line !== undefined ? e.mark.line + 1 : undefined;
    const column = e.mark?.column !== undefined ? e.mark.column + 1 : undefined;
    return { ok: false, error: e.reason || e.message, line, column };
  }
  if (e instanceof Error) return { ok: false, error: e.message };
  return { ok: false, error: String(e) };
}

function clampIndent(n: number | undefined): number {
  if (n === undefined) return DEFAULT_INDENT;
  if (n < 1) return 1;
  if (n > 10) return 10;
  return Math.floor(n);
}
